import { get, post } from 'aws-amplify/api';
import useSWR from 'swr';
import { processResponse } from './api_util';
import { getOptimisticInfiniteData, deriveIsProposedTransfer } from './transactions.js';

// Client for the manual transfer review/adjust API (human verdict layer over the
// AI-proposed internal transfers). See
// .claude/references/transfer-review-manual-adjust.md (ADR-001).
const API_NAME = 'LivisiblyTransferReviewApi';

// Ranked counterpart candidates for a transfer leg (same q-score + pre-filter the
// proposer uses), each flagged available/paired. Fetched on demand by the picker.
export async function getTransferCandidates(txnId) {
  const restOperation = get({
    apiName: API_NAME,
    path: `transfers/candidates?txn_id=${encodeURIComponent(txnId)}`,
  });
  const response = await restOperation.response;
  return processResponse(response);
}

// Load a leg's ranked candidate set on demand (when the match-review dialog opens).
export function useTransferCandidates(txnId, shouldFetch = true) {
  return useSWR(
    shouldFetch && txnId ? ['TRANSFER_CANDIDATES', txnId] : null,
    ([, id]) => getTransferCandidates(id),
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );
}

async function confirmTransfer(outTxnId, inTxnId) {
  const restOperation = post({
    apiName: API_NAME,
    path: 'transfers/confirm',
    options: { body: { out_txn_id: outTxnId, in_txn_id: inTxnId } },
  });
  const response = await restOperation.response;
  return processResponse(response);
}

async function linkTransfer(outTxnId, inTxnId) {
  const restOperation = post({
    apiName: API_NAME,
    path: 'transfers/link',
    options: { body: { out_txn_id: outTxnId, in_txn_id: inTxnId } },
  });
  const response = await restOperation.response;
  return processResponse(response);
}

async function rejectTransfer(txnIdA, txnIdB, reason) {
  const restOperation = post({
    apiName: API_NAME,
    path: 'transfers/reject',
    options: { body: { txn_id_a: txnIdA, txn_id_b: txnIdB, reason } },
  });
  const response = await restOperation.response;
  return processResponse(response);
}

// --- Optimistic cache transforms over the enriched-transactions infinite cache ---
// The review endpoints return small acks (not enriched rows), so we optimistically
// patch the cache and then revalidate to true up (fire-then-revalidate, mirroring
// the entity-ownership manager). disposition lives on transfer_partner.

const isPair = (row, idA, idB) =>
  row.transfer_partner &&
  (row.transaction.id === idA || row.transaction.id === idB) &&
  (row.transfer_partner.transaction.id === idA || row.transfer_partner.transaction.id === idB);

function setDispositionForPair(rows, outId, inId, disposition, manual = false) {
  return rows.map((row) => {
    if (!isPair(row, outId, inId)) return row;
    const patched = { ...row, transfer_partner: { ...row.transfer_partner, disposition, manual } };
    // Re-derive the flat filter field so a just-confirmed pair drops out of the
    // "Proposed Transfers Only" filter immediately, without waiting on a refetch.
    return { ...patched, isProposedTransfer: deriveIsProposedTransfer(patched) };
  });
}

function clearLinkForLegs(rows, aId, bId) {
  const ids = [aId, bId];
  return rows.map((row) =>
    ids.includes(row.transaction.id)
      ? { ...row, transfer_partner: null, hasTransferPartner: false, isProposedTransfer: false }
      : row
  );
}

// A minimal transfer_partner built from a leg's display data — enough for the
// paired-row render (account name, sub-account, date, description, amount, badge).
// The link revalidation fills in the rest (merchant/category/flag) shortly after.
function minimalPartner(leg) {
  if (!leg) return null;
  return {
    transaction: {
      id: leg.txn_id,
      posting_date: leg.posting_date,
      description: leg.description,
      amount: leg.amount,
    },
    bankAccount: { _displayName: leg.account_display, sub_account_number: leg.sub_account_number ?? null },
    disposition: 'CONFIRMED',
    manual: true,
  };
}

// Optimistically re-point a transfer to a new pair: both legs show each other as
// a CONFIRMED/manual partner; any prior partner of either leg is freed.
function relinkPair(rows, anchor, candidate) {
  const pairIds = [anchor.txn_id, candidate.txn_id];
  return rows.map((row) => {
    const id = row.transaction?.id;
    if (id === anchor.txn_id) return { ...row, hasTransferPartner: true, isProposedTransfer: false, transfer_partner: minimalPartner(candidate) };
    if (id === candidate.txn_id) return { ...row, hasTransferPartner: true, isProposedTransfer: false, transfer_partner: minimalPartner(anchor) };
    const partnerId = row.transfer_partner?.transaction?.id;
    if (partnerId && pairIds.includes(partnerId)) return { ...row, hasTransferPartner: false, isProposedTransfer: false, transfer_partner: null };
    return row;
  });
}

// Bind the review mutations to an enriched-transactions SWR-infinite `mutate`.
// Each returns the mutate promise so callers can await / .catch for a toast.
export function useTransferReviewActions(mutateTransactions) {
  const applyOptimistic = (serverPromise, updateFn, revalidate) =>
    mutateTransactions(serverPromise, {
      optimisticData: (data) => getOptimisticInfiniteData(data, updateFn),
      revalidate,
      populateCache: false,
      rollbackOnError: true,
    });

  // Confirm/reject are exact optimistic edits — no refetch needed.
  const confirm = (outTxnId, inTxnId) =>
    applyOptimistic(
      confirmTransfer(outTxnId, inTxnId),
      (rows) => setDispositionForPair(rows, outTxnId, inTxnId, 'CONFIRMED'),
      false
    );

  const reject = (txnIdA, txnIdB, reason) =>
    applyOptimistic(
      rejectTransfer(txnIdA, txnIdB, reason),
      (rows) => clearLinkForLegs(rows, txnIdA, txnIdB),
      false
    );

  // Re-point to a new candidate. `anchor` and `candidate` are slim display legs
  // ({txn_id, posting_date, description, amount, account_display, sub_account_number}).
  // Shows the new pair immediately, then revalidates to fill full enrichment.
  const link = (anchor, candidate) =>
    applyOptimistic(
      linkTransfer(anchor.txn_id, candidate.txn_id),
      (rows) => relinkPair(rows, anchor, candidate),
      true
    );

  return { confirm, link, reject };
}
